import { View } from 'react-native';
import { Props, Breeder, MediaAsset } from 'shared/@types';
import { useTranslation } from 'react-i18next';
import { Text } from 'shared/components/text';
import { H2 } from 'shared/components/title';
import { PageBuilder } from 'shared/library/page-builder';
import { apiRoute } from 'shared/library/api-route';
import { MapPosition } from 'shared/components/map-position';
import { getCountryName } from 'shared/library/i18n/country-name';
import { Image } from 'shared/components/image';

interface BreederProfileAboutScreenProps extends Props {
  breeder: Breeder;
  mediaAssets: MediaAsset[];
  showMediaPreview?: boolean;
}

const BreederProfileAboutScreen = ({
  breeder,
  mediaAssets,
  showMediaPreview = false,
}: BreederProfileAboutScreenProps) => {
  const { t } = useTranslation('screens');
  const owners = breeder.owners ?? [];
  const users = breeder.users ?? [];
  const owner = owners[0] ?? users[0] ?? null;
  const locationText = owner ? getLocationText(owner, t) : null;
  const previewMediaAssets = showMediaPreview ? mediaAssets.slice(1, 4) : [];

  return (
    <View className={'gap-4 md:gap-6'}>
      <H2 title={t('breeders.show_breeder.about_us')} marginTop={false} marginBottom={false} />

      <View className={'gap-6'}>
        <Text className={'text-brand-brown-dark max-w-5xl leading-7 md:text-lg lg:flex-1'}>
          {breeder.description ?? t('breeders.show_breeder.no_description')}
        </Text>

        {previewMediaAssets.length > 0 && (
          <View className={'flex-row gap-2'}>
            {previewMediaAssets.map((mediaAsset) => (
              <Image
                key={mediaAsset.uuid}
                src={mediaAsset}
                alt={breeder.name}
                containerClassName={'aspect-square flex-1'}
                imageClassName={'rounded-lg object-cover'}
                sizeType={'card'}
                aspectRatio={1}
              />
            ))}
          </View>
        )}

        {owner?.approximate_location && (
          <View className={'gap-4 md:gap-6'}>
            <H2
              title={t('breeders.show_breeder.our_location')}
              marginTop={false}
              marginBottom={false}
            />

            {locationText && (
              <Text className={'text-brand-brown-dark md:text-lg'}>{locationText}</Text>
            )}

            <MapPosition
              containerClassName={'aspect-4/3 w-full md:max-w-3xl'}
              imageClassName={'rounded-lg object-cover'}
              coordinate={owner.approximate_location}
            />
          </View>
        )}
      </View>
    </View>
  );
};

function getLocationText(
  owner: NonNullable<Breeder['owners'][number] | Breeder['users'][number]>,
  t: ReturnType<typeof useTranslation>['t'],
) {
  return [owner.city, getCountryName(t, owner.country)].filter(Boolean).join(', ');
}

BreederProfileAboutScreen.pageBuilder = new PageBuilder<
  BreederProfileAboutScreenProps,
  { breeder: string }
>().request(({ req, params }) =>
  req<{ breeder: Breeder; media_assets: MediaAsset[] }>(
    apiRoute('breeders.show', { breeder: params.breeder }),
    {
      compact: true,
      include: 'media_assets',
    },
  ).then((data) => ({
    breeder: data.breeder,
    mediaAssets: data.media_assets,
  })),
);

export { BreederProfileAboutScreen };
export type { BreederProfileAboutScreenProps };
